import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'
import {
  IHrAnalyticsOverviewResponse,
  ILogisticsAccommodationStatsByCampSingleResponse,
  ILogisticsAccommodationSummaryResponse,
  ILogisticsFoodStatsByDaySingleResponse,
  ILogisticsFoodStatsByMealSingleResponse,
  ILogisticsFoodStatsByWeekSingleResponse,
  ILogisticsFoodSummaryResponse,
  IOvertimeByDepartmentSingleResponse,
  IPayrollAnalyticsParams,
  IPayrollByDepartmentSingleResponse,
  IPayrollByRoleSingleResponse,
  IPayrollPeriodMonthSingleResponse,
  IPayrollPeriodParams,
  IPayrollSummaryResponse,
  IScheduleAnalyticsParams,
  IScheduleByDepartmentSingleResponse,
  IScheduleByRoleSingleResponse,
  IScheduleDailyCoverageSingleResponse,
  IScheduleSummaryResponse,
} from 'types/api/hrAnalyticsApi.interface'
import { IHrAnalyticsState } from './hrAnalytics.types'

const startLoading = (state: IHrAnalyticsState) => {
  state.loadingCount += 1
  state.isLoading = true
}

const stopLoading = (state: IHrAnalyticsState) => {
  state.loadingCount = Math.max(0, state.loadingCount - 1)
  state.isLoading = state.loadingCount > 0
}

const initialState: IHrAnalyticsState = {
  isLoading: false,
  loadingCount: 0,
  overview: null,
  scheduleSummary: null,
  scheduleByDepartment: [],
  scheduleByRole: [],
  scheduleDailyCoverage: [],
  payrollSummary: null,
  payrollByDepartment: [],
  payrollByRole: [],
  payrollPeriod: [],
  overtimeByDepartment: [],
  logisticsFoodSummary: null,
  logisticsFoodStatsByDay: [],
  logisticsFoodStatsByWeek: [],
  logisticsFoodStatsByMeal: [],
  logisticsAccommodationSummary: null,
  logisticsAccommodationStatsByCamp: [],
}

export const getOverview = createAsyncThunk(
  'hrAnalytics/getOverview',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IHrAnalyticsOverviewResponse> = await baseUrl.get(
        'hr/analytics/overview',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleSummary = createAsyncThunk(
  'hrAnalytics/getScheduleSummary',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleSummaryResponse> = await baseUrl.get(
        'hr/analytics/schedule/summary',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleByDepartment = createAsyncThunk(
  'hrAnalytics/getScheduleByDepartment',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleByDepartmentSingleResponse[]> = await baseUrl.get(
        'hr/analytics/schedule/by-department',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleByRole = createAsyncThunk(
  'hrAnalytics/getScheduleByRole',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleByRoleSingleResponse[]> = await baseUrl.get(
        'hr/analytics/schedule/by-role',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleDailyCoverage = createAsyncThunk(
  'hrAnalytics/getScheduleDailyCoverage',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleDailyCoverageSingleResponse[]> = await baseUrl.get(
        'hr/analytics/schedule/daily-coverage',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getPayrollSummary = createAsyncThunk(
  'hrAnalytics/getPayrollSummary',
  async (params: IPayrollAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPayrollSummaryResponse> = await baseUrl.get(
        'hr/analytics/payroll/summary',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getPayrollByDepartment = createAsyncThunk(
  'hrAnalytics/getPayrollByDepartment',
  async (params: IPayrollAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPayrollByDepartmentSingleResponse[]> = await baseUrl.get(
        'hr/analytics/payroll/by-department',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getPayrollByRole = createAsyncThunk(
  'hrAnalytics/getPayrollByRole',
  async (params: IPayrollAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPayrollByRoleSingleResponse[]> = await baseUrl.get(
        'hr/analytics/payroll/by-role',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getPayrollPeriod = createAsyncThunk(
  'hrAnalytics/getPayrollPeriod',
  async (params: IPayrollPeriodParams, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPayrollPeriodMonthSingleResponse[]> = await baseUrl.get(
        'hr/analytics/payroll/period',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOvertimeByDepartment = createAsyncThunk(
  'hrAnalytics/getOvertimeByDepartment',
  async (params: IPayrollAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IOvertimeByDepartmentSingleResponse[]> = await baseUrl.get(
        'hr/analytics/payroll/overtime/by-department',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLogisticsFoodSummary = createAsyncThunk(
  'hrAnalytics/getLogisticsFoodSummary',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<ILogisticsFoodSummaryResponse> = await baseUrl.get(
        'hr/analytics/logistics/food/summary',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLogisticsFoodStatsByDay = createAsyncThunk(
  'hrAnalytics/getLogisticsFoodStatsByDay',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<ILogisticsFoodStatsByDaySingleResponse[]> = await baseUrl.get(
        'hr/analytics/logistics/food/stats/by-day',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLogisticsFoodStatsByWeek = createAsyncThunk(
  'hrAnalytics/getLogisticsFoodStatsByWeek',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<ILogisticsFoodStatsByWeekSingleResponse[]> = await baseUrl.get(
        'hr/analytics/logistics/food/stats/by-week',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLogisticsFoodStatsByMeal = createAsyncThunk(
  'hrAnalytics/getLogisticsFoodStatsByMeal',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<ILogisticsFoodStatsByMealSingleResponse[]> = await baseUrl.get(
        'hr/analytics/logistics/food/stats/by-meal',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLogisticsAccommodationSummary = createAsyncThunk(
  'hrAnalytics/getLogisticsAccommodationSummary',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<ILogisticsAccommodationSummaryResponse> = await baseUrl.get(
        'hr/analytics/logistics/accommodation/summary',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getLogisticsAccommodationStatsByCamp = createAsyncThunk(
  'hrAnalytics/getLogisticsAccommodationStatsByCamp',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<ILogisticsAccommodationStatsByCampSingleResponse[]> =
        await baseUrl.get('hr/analytics/logistics/accommodation/stats/by-camp', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const hrAnalyticsSlice = createSlice({
  name: 'hrAnalytics',
  initialState,
  reducers: {
    clearHrAnalytics: () => initialState,
  },
  extraReducers: {
    [getOverview.pending.type]: (state) => {
      startLoading(state)
    },
    [getOverview.fulfilled.type]: (
      state,
      { payload }: { payload: IHrAnalyticsOverviewResponse },
    ) => {
      stopLoading(state)
      state.overview = payload
    },
    [getOverview.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getScheduleSummary.pending.type]: (state) => {
      startLoading(state)
    },
    [getScheduleSummary.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleSummaryResponse },
    ) => {
      stopLoading(state)
      state.scheduleSummary = payload
    },
    [getScheduleSummary.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getScheduleByDepartment.pending.type]: (state) => {
      startLoading(state)
    },
    [getScheduleByDepartment.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleByDepartmentSingleResponse[] },
    ) => {
      stopLoading(state)
      state.scheduleByDepartment = payload
    },
    [getScheduleByDepartment.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getScheduleByRole.pending.type]: (state) => {
      startLoading(state)
    },
    [getScheduleByRole.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleByRoleSingleResponse[] },
    ) => {
      stopLoading(state)
      state.scheduleByRole = payload
    },
    [getScheduleByRole.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getScheduleDailyCoverage.pending.type]: (state) => {
      startLoading(state)
    },
    [getScheduleDailyCoverage.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleDailyCoverageSingleResponse[] },
    ) => {
      stopLoading(state)
      state.scheduleDailyCoverage = payload
    },
    [getScheduleDailyCoverage.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getPayrollSummary.pending.type]: (state) => {
      startLoading(state)
    },
    [getPayrollSummary.fulfilled.type]: (
      state,
      { payload }: { payload: IPayrollSummaryResponse },
    ) => {
      stopLoading(state)
      state.payrollSummary = payload
    },
    [getPayrollSummary.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getPayrollByDepartment.pending.type]: (state) => {
      startLoading(state)
    },
    [getPayrollByDepartment.fulfilled.type]: (
      state,
      { payload }: { payload: IPayrollByDepartmentSingleResponse[] },
    ) => {
      stopLoading(state)
      state.payrollByDepartment = payload
    },
    [getPayrollByDepartment.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getPayrollByRole.pending.type]: (state) => {
      startLoading(state)
    },
    [getPayrollByRole.fulfilled.type]: (
      state,
      { payload }: { payload: IPayrollByRoleSingleResponse[] },
    ) => {
      stopLoading(state)
      state.payrollByRole = payload
    },
    [getPayrollByRole.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getPayrollPeriod.pending.type]: (state) => {
      startLoading(state)
    },
    [getPayrollPeriod.fulfilled.type]: (
      state,
      { payload }: { payload: IPayrollPeriodMonthSingleResponse[] },
    ) => {
      stopLoading(state)
      state.payrollPeriod = payload
    },
    [getPayrollPeriod.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getOvertimeByDepartment.pending.type]: (state) => {
      startLoading(state)
    },
    [getOvertimeByDepartment.fulfilled.type]: (
      state,
      { payload }: { payload: IOvertimeByDepartmentSingleResponse[] },
    ) => {
      stopLoading(state)
      state.overtimeByDepartment = payload
    },
    [getOvertimeByDepartment.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getLogisticsFoodSummary.pending.type]: (state) => {
      startLoading(state)
    },
    [getLogisticsFoodSummary.fulfilled.type]: (
      state,
      { payload }: { payload: ILogisticsFoodSummaryResponse },
    ) => {
      stopLoading(state)
      state.logisticsFoodSummary = payload
    },
    [getLogisticsFoodSummary.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getLogisticsFoodStatsByDay.pending.type]: (state) => {
      startLoading(state)
    },
    [getLogisticsFoodStatsByDay.fulfilled.type]: (
      state,
      { payload }: { payload: ILogisticsFoodStatsByDaySingleResponse[] },
    ) => {
      stopLoading(state)
      state.logisticsFoodStatsByDay = payload
    },
    [getLogisticsFoodStatsByDay.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getLogisticsFoodStatsByWeek.pending.type]: (state) => {
      startLoading(state)
    },
    [getLogisticsFoodStatsByWeek.fulfilled.type]: (
      state,
      { payload }: { payload: ILogisticsFoodStatsByWeekSingleResponse[] },
    ) => {
      stopLoading(state)
      state.logisticsFoodStatsByWeek = payload
    },
    [getLogisticsFoodStatsByWeek.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getLogisticsFoodStatsByMeal.pending.type]: (state) => {
      startLoading(state)
    },
    [getLogisticsFoodStatsByMeal.fulfilled.type]: (
      state,
      { payload }: { payload: ILogisticsFoodStatsByMealSingleResponse[] },
    ) => {
      stopLoading(state)
      state.logisticsFoodStatsByMeal = payload
    },
    [getLogisticsFoodStatsByMeal.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getLogisticsAccommodationSummary.pending.type]: (state) => {
      startLoading(state)
    },
    [getLogisticsAccommodationSummary.fulfilled.type]: (
      state,
      { payload }: { payload: ILogisticsAccommodationSummaryResponse },
    ) => {
      stopLoading(state)
      state.logisticsAccommodationSummary = payload
    },
    [getLogisticsAccommodationSummary.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
    [getLogisticsAccommodationStatsByCamp.pending.type]: (state) => {
      startLoading(state)
    },
    [getLogisticsAccommodationStatsByCamp.fulfilled.type]: (
      state,
      { payload }: { payload: ILogisticsAccommodationStatsByCampSingleResponse[] },
    ) => {
      stopLoading(state)
      state.logisticsAccommodationStatsByCamp = payload
    },
    [getLogisticsAccommodationStatsByCamp.rejected.type]: (state, { payload }) => {
      stopLoading(state)
      handleErrors(payload)
    },
  },
})

export const { clearHrAnalytics } = hrAnalyticsSlice.actions
export default hrAnalyticsSlice.reducer
